import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Autocomplete, Box, Button, FormControl, Grid2, IconButton, InputLabel, MenuItem, Modal, Select, TextField,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateField } from "@mui/x-date-pickers/DateField";
import axios from "axios";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import { jwtDecode } from "jwt-decode";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../Auth/AuthContext";

const API = "https://api.sigmasystems.com.br/api/ag-barber";

const style = {
  position: "absolute",
  top:  { xs: "50%", md: "50%" },
  left: { xs: "50%", md: "50%" },
  transform: { xs: "translate(-50%, -50%)", md: "translate(-50%, -50%)" },
  width:     { xs: "95vw", md: 815 },
  height:    { xs: "auto", md: "auto" },
  maxHeight: { xs: "740", md: "90vh" },
  overflowY: "auto",
  boxSizing: "border-box",
  bgcolor: "background.paper",
  border: { xs: "none", md: "2px solid #4A7011" },
  borderRadius: { xs: 0, md: "8px" },
  boxShadow: 24,
  p: { xs: 2, md: 4 },
};

const btnPrimario = {
  color: "#fff", height: 40, minWidth: 110, borderRadius: "5px", fontSize: "15px", fontWeight: "bold",
  backgroundColor: "#4a7011", textTransform: "none", "&:hover": { backgroundColor: "#293D09" },
};
const btnSecundario = {
  color: "#4a7011", height: 40, minWidth: 110, border: "solid 2px #4a7011", borderRadius: "5px",
  fontSize: "15px", fontWeight: "bold", backgroundColor: "transparent", textTransform: "none",
  "&:hover": { backgroundColor: "#eeeeee" },
};
const btnAdd = { ...btnPrimario, minWidth: 0, width: 90, height: 56 };

/* ---------- helpers ---------- */

const mensagemErro = (error, padrao) => error.response?.data?.message || padrao;
const brl = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const dataBR = (iso) => (iso ? iso.split("-").reverse().join("/") : "");

const soDigitos = (s) => String(s ?? "").replace(/\D/g, "").slice(0, 11);
// (99) 9999-9999 (10 dígitos) ou (99) 99999-9999 (11 dígitos), formatando enquanto digita
function mascaraTelefone(valor) {
  const d = soDigitos(valor);
  if (d.length === 0) return "";
  if (d.length <= 2) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

function lerSessao() {
  try {
    return jwtDecode(sessionStorage.getItem("token"));
  } catch {
    return null;
  }
}

// Busca com espera de 300 ms depois da última tecla (não chama a API a cada letra)
function useDebounce(valor, ms = 300) {
  const [v, setV] = useState(valor);
  useEffect(() => {
    const t = setTimeout(() => setV(valor), ms);
    return () => clearTimeout(t);
  }, [valor, ms]);
  return v;
}

const LANC_VAZIO = { client: null, user_id: "", date: dayjs(), servicoSel: "", produtoSel: "", services: [], products: [] };

/* ---------- componente ---------- */

function Lancamentos() {
  const { nome, logout } = useAuth();
  const navigate = useNavigate();

  const sessao = useMemo(lerSessao, []);
  const ehAdmin = sessao?.tipo === "ADMIN";
  const meuId = sessao?.id ?? null;

  // dados de apoio
  const [profissionais, setProfissionais] = useState([]);
  const [servicosCad, setServicosCad] = useState([]);
  const [produtosCad, setProdutosCad] = useState([]);
  const [clientesTodos, setClientesTodos] = useState([]);   // para escolher o cliente no lançamento

  // buscas e listas
  const [buscaCliente, setBuscaCliente] = useState("");
  const [buscaLanc, setBuscaLanc] = useState("");
  const dBuscaCliente = useDebounce(buscaCliente);
  const dBuscaLanc = useDebounce(buscaLanc);
  const [resCliente, setResCliente] = useState([]);
  const [resLanc, setResLanc] = useState([]);
  const [ultimos, setUltimos] = useState([]);
  const [versao, setVersao] = useState(0);
  const [erroTela, setErroTela] = useState("");

  // modal de lançamento
  const [openLanc, setOpenLanc] = useState(false);
  const [editandoLanc, setEditandoLanc] = useState(null);   // { id, commission_pct, commission_value } ou null
  const [formLanc, setFormLanc] = useState(LANC_VAZIO);
  const [erroLanc, setErroLanc] = useState("");
  const [salvandoLanc, setSalvandoLanc] = useState(false);

  // modal de cliente
  const [openCli, setOpenCli] = useState(false);
  const [editandoCli, setEditandoCli] = useState(null);     // id ou null
  const [formCli, setFormCli] = useState({ name: "", phone: "" });
  const [erroCli, setErroCli] = useState("");
  const [salvandoCli, setSalvandoCli] = useState(false);

  /* ----- carga ----- */

  useEffect(() => {
    Promise.all([
      axios.post(`${API}/get-users`, {}),
      axios.post(`${API}/get-services`, {}),
      axios.post(`${API}/get-products`, {}),
    ])
      .then(([u, s, p]) => { setProfissionais(u.data); setServicosCad(s.data); setProdutosCad(p.data); })
      .catch((e) => setErroTela(mensagemErro(e, "Erro ao carregar os dados.")));
  }, []);

  const carregarClientesTodos = useCallback(() => {
    axios.get(`${API}/clients`, { params: { limit: 1000 } })
      .then((r) => setClientesTodos(r.data))
      .catch(() => {});
  }, []);
  useEffect(carregarClientesTodos, [carregarClientesTodos, versao]);

  useEffect(() => {
    axios.get(`${API}/sales`, { params: { limit: 30 } })
      .then((r) => setUltimos(r.data))
      .catch((e) => setErroTela(mensagemErro(e, "Erro ao carregar lançamentos.")));
  }, [versao]);

  useEffect(() => {
    if (!dBuscaCliente.trim()) { setResCliente([]); return undefined; }
    let ativo = true;   // ignora resposta antiga se o usuário já digitou outra coisa
    axios.get(`${API}/clients`, { params: { q: dBuscaCliente.trim(), limit: 20 } })
      .then((r) => ativo && setResCliente(r.data))
      .catch(() => ativo && setResCliente([]));
    return () => { ativo = false; };
  }, [dBuscaCliente, versao]);

  useEffect(() => {
    if (!dBuscaLanc.trim()) { setResLanc([]); return undefined; }
    let ativo = true;
    axios.get(`${API}/sales`, { params: { q: dBuscaLanc.trim(), limit: 20 } })
      .then((r) => ativo && setResLanc(r.data))
      .catch(() => ativo && setResLanc([]));
    return () => { ativo = false; };
  }, [dBuscaLanc, versao]);

  /* ----- cliente: abrir / salvar ----- */

  const abrirNovoCliente = () => {
    setEditandoCli(null); setFormCli({ name: "", phone: "" }); setErroCli(""); setOpenCli(true);
  };
  const abrirEditarCliente = (c) => {
    if (!ehAdmin) return;   // EMPLOYEE não edita cliente (nem vê o telefone)
    setEditandoCli(c.id); setFormCli({ name: c.name, phone: mascaraTelefone(c.phone) });
    setErroCli(""); setOpenCli(true);
  };

  const salvarCliente = async () => {
    const name = formCli.name.trim();
    const digitos = soDigitos(formCli.phone);
    if (!name) return setErroCli("Informe o nome do cliente.");
    if (name.length > 145) return setErroCli("O nome pode ter no máximo 145 caracteres.");
    if (digitos.length < 10) return setErroCli("Informe o telefone completo.");

    setSalvandoCli(true); setErroCli("");
    try {
      if (editandoCli) {
        await axios.post(`${API}/update-client`, { id: editandoCli, formEditClient: { name, phone: digitos } });
      } else {
        const r = await axios.post(`${API}/register-client`, { formAddClient: { name, phone: digitos } });
        // se o modal de lançamento estiver aberto por baixo, já deixa o novo cliente selecionado
        setFormLanc((f) => (openLanc ? { ...f, client: { id: r.data.id, name } } : f));
      }
      setOpenCli(false);
      setVersao((v) => v + 1);
    } catch (e) {
      setErroCli(mensagemErro(e, "Erro ao salvar cliente."));
    } finally {
      setSalvandoCli(false);
    }
  };

  /* ----- lançamento: abrir / itens / salvar ----- */

  const abrirNovoLanc = () => {
    setEditandoLanc(null);
    // EMPLOYEE já começa com ele mesmo como profissional (pode trocar)
    setFormLanc({ ...LANC_VAZIO, date: dayjs(), user_id: !ehAdmin && meuId ? meuId : "" });
    setErroLanc(""); setOpenLanc(true);
  };

  const abrirEditarLanc = async (item) => {
    if (!item.can_edit) return;
    try {
      const { data } = await axios.get(`${API}/sale`, { params: { id: item.id } });
      const cli = clientesTodos.find((c) => c.id === data.client_id) ?? { id: data.client_id, name: item.client_name };
      setEditandoLanc({ id: data.id, commission_pct: data.commission_pct, commission_value: data.commission_value });
      setFormLanc({
        client: cli, user_id: data.user_id, date: dayjs(data.date), servicoSel: "", produtoSel: "",
        services: data.services.map((s) => ({ id: s.service_id, name: s.name, price: s.price, qty: s.qty })),
        products: data.products.map((p) => ({ id: p.product_id, name: p.name, price: p.price, qty: p.qty })),
      });
      setErroLanc(""); setOpenLanc(true);
    } catch (e) {
      setErroTela(mensagemErro(e, "Não foi possível abrir o lançamento."));
    }
  };

  const servicosDoProfissional = useMemo(() => {
    const p = profissionais.find((x) => x.id === formLanc.user_id);
    return p ? servicosCad.filter((s) => p.services.includes(s.id)) : [];
  }, [profissionais, servicosCad, formLanc.user_id]);

  const trocarProfissional = (id) => {
    const p = profissionais.find((x) => x.id === id);
    // ao trocar de profissional, tira da lista os serviços que o novo não faz
    setFormLanc((f) => ({
      ...f, user_id: id, servicoSel: "",
      services: f.services.filter((s) => p?.services.includes(s.id)),
    }));
  };

  // soma 1 na quantidade se o item já está na lista
  const adicionarItem = (campoLista, campoSel, catalogo) => {
    const id = formLanc[campoSel];
    const item = catalogo.find((x) => x.id === id);
    if (!item) return;
    setFormLanc((f) => {
      const lista = f[campoLista];
      const existe = lista.some((x) => x.id === id);
      return {
        ...f, [campoSel]: "",
        [campoLista]: existe
          ? lista.map((x) => (x.id === id ? { ...x, qty: Math.min(x.qty + 1, 99) } : x))
          : [...lista, { id, name: item.name, price: Number(item.price), qty: 1 }],
      };
    });
  };
  const mudarQtd = (campoLista, id, delta) =>
    setFormLanc((f) => ({
      ...f,
      [campoLista]: f[campoLista].map((x) => (x.id === id ? { ...x, qty: Math.min(Math.max(x.qty + delta, 1), 99) } : x)),
    }));
  const removerItem = (campoLista, id) =>
    setFormLanc((f) => ({ ...f, [campoLista]: f[campoLista].filter((x) => x.id !== id) }));

  const totalLanc = useMemo(
    () => [...formLanc.services, ...formLanc.products].reduce((t, x) => t + x.price * x.qty, 0),
    [formLanc.services, formLanc.products]);

  const salvarLanc = async () => {
    if (!formLanc.client) return setErroLanc("Selecione o cliente.");
    if (!formLanc.user_id) return setErroLanc("Selecione o profissional.");
    if (!formLanc.date || !formLanc.date.isValid()) return setErroLanc("Informe a data.");
    if (formLanc.date.isAfter(dayjs(), "day")) return setErroLanc("A data não pode ser futura.");
    if (!formLanc.services.length && !formLanc.products.length)
      return setErroLanc("Adicione pelo menos um serviço ou produto.");

    // o servidor recalcula os preços; aqui vão só ids e quantidades
    const dados = {
      client_id: formLanc.client.id,
      user_id: formLanc.user_id,
      date: formLanc.date.format("YYYY-MM-DD"),
      services: formLanc.services.map((s) => ({ service_id: s.id, qty: s.qty })),
      products: formLanc.products.map((p) => ({ product_id: p.id, qty: p.qty })),
    };

    setSalvandoLanc(true); setErroLanc("");
    try {
      if (editandoLanc) await axios.post(`${API}/update-sale`, { id: editandoLanc.id, formEditSale: dados });
      else await axios.post(`${API}/register-sale`, { formAddSale: dados });
      setOpenLanc(false);
      setVersao((v) => v + 1);
    } catch (e) {
      setErroLanc(mensagemErro(e, "Erro ao salvar lançamento."));
    } finally {
      setSalvandoLanc(false);
    }
  };

  /* ----- partes da tela ----- */

  const LinhaLanc = ({ l }) => (
    <li
      className={`lanc-item ${l.can_edit ? "editavel" : ""}`}
      onClick={() => abrirEditarLanc(l)}
      title={l.can_edit ? "Editar lançamento" : "Você não participou deste lançamento"}
    >
      <span className="lanc-item__txt">
        <span className="lanc-item__data">{dataBR(l.date)}</span>{" "}
        <strong>
          {l.client_name}
          {l.services ? ` - ${l.services}` : ""}
          {l.products_qty > 0 ? ` - ${l.products_qty} ${l.products_qty === 1 ? "Produto" : "Produtos"}` : ""}
        </strong>
      </span>
      <span className="lanc-item__fim">
        <span className="lanc-item__prof">{l.professional_name} -</span>{" "}
        <strong className="lanc-item__valor">{brl(l.total)}</strong>
      </span>
    </li>
  );

  const LinhaCliente = ({ c }) => (
    <li className={`lanc-item ${ehAdmin ? "editavel" : ""}`} onClick={() => abrirEditarCliente(c)}>
      <span className="lanc-item__txt">
        <strong>{c.name}{ehAdmin && c.phone ? ` - ${mascaraTelefone(c.phone)}` : ""}</strong>
      </span>
    </li>
  );

  // tabela de itens (serviços ou produtos) do modal de lançamento
  const ListaItens = ({ titulo, campo }) =>
    formLanc[campo].length > 0 && (
      <div className="lanc-itens">
        <div className="lanc-itens__titulo">{titulo}</div>
        {formLanc[campo].map((it) => (
          <div className="lanc-itens__linha" key={it.id}>
            <span className="lanc-itens__nome">{it.name}</span>
            <span className="lanc-itens__qtd">
              <IconButton size="small" onClick={() => mudarQtd(campo, it.id, -1)} aria-label="Diminuir">−</IconButton>
              <b>{it.qty}</b>
              <IconButton size="small" onClick={() => mudarQtd(campo, it.id, 1)} aria-label="Aumentar">+</IconButton>
            </span>
            <span className="lanc-itens__preco">{brl(it.price * it.qty)}</span>
            <IconButton size="small" onClick={() => removerItem(campo, it.id)} aria-label="Remover">✕</IconButton>
          </div>
        ))}
      </div>
    );

  const semResultado = (texto) => <li className="lanc-vazio">{texto}</li>;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
      <div className="container-ag">
        <div className="content-ag">
          <div className="sideMenu">
            <div className="sideMenu-content">
              <div className="sideMenu-orientation">
                <div className="sideMenu-top">
                  <img src="/Images/bot-3d.png" className="sideMenu-bot3d" alt="" />
                  <h3 className="sideMenu-name">{nome}</h3>
                </div>
                <div className="sideMenu-btns">
                  <Link className="sideMenu-option" to="/Agendamentos">
                    <img src="/Images/calendar.svg" className="sideMenu-btn-icon" alt="" />
                    <span className="sideMenu-title">Agendamentos</span>
                  </Link>
                  {ehAdmin && (
                    <>
                    <Link className="sideMenu-option" to="/Configuracoes">
                      <img src="/Images/options.svg" className="sideMenu-btn-icon" alt="" />
                      <span className="sideMenu-title">Configuração</span>
                    </Link>
                    <Link className="sideMenu-option-active" to="/Lancamentos">
                      <img src="/Images/bag.svg" className="sideMenu-btn-icon" alt="" />
                      <span className="sideMenu-title">Lançamentos</span>
                    </Link>
                    </>
                  )}
                </div>
              </div>
              <div className="sideMenu-logout">
                <div className="sideMenu-logout-btn" onClick={() => { logout(); navigate("/"); }}>
                  <img src="/Images/logout.svg" className="sideMenu-logout-icon" alt="" />
                  <span className="sideMenu-title">Sair</span>
                </div>
                <img className="sideMenu-logo" src="/Images/sigmaBranco.png" alt="Sigma Systems" />
              </div>
            </div>
          </div>

          <div className="content">
            <div className="content-top">
              <h1 className="content-title">Lançamentos</h1>
              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                <Button onClick={abrirNovoLanc} sx={{ ...btnPrimario, minWidth: 150 }}>+ Lançamento</Button>
                <Button onClick={abrirNovoCliente} sx={{ ...btnPrimario, minWidth: 130 }}>+ Cliente</Button>
              </div>
            </div>

            <div className="content-block lanc">
              {erroTela && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setErroTela("")}>{erroTela}</Alert>}

              <div className="lanc-buscas">
                <section className="lanc-busca">
                  <h2>Procurar cliente</h2>
                  <input className="lanc-input" placeholder={ehAdmin ? "Nome ou telefone..." : "Nome..."}
                    value={buscaCliente} onChange={(e) => setBuscaCliente(e.target.value)} />
                  {buscaCliente.trim() && (
                    <ul className="lanc-lista">
                      {resCliente.length === 0 ? semResultado("Nenhum cliente encontrado.")
                        : resCliente.map((c) => <LinhaCliente key={c.id} c={c} />)}
                    </ul>
                  )}
                </section>

                <section className="lanc-busca">
                  <h2>Procurar lançamento</h2>
                  <input className="lanc-input" placeholder="Nome do cliente ou data (ex: 20/10/2026)..."
                    value={buscaLanc} onChange={(e) => setBuscaLanc(e.target.value)} />
                  {buscaLanc.trim() && (
                    <ul className="lanc-lista">
                      {resLanc.length === 0 ? semResultado("Nenhum lançamento encontrado.")
                        : resLanc.map((l) => <LinhaLanc key={l.id} l={l} />)}
                    </ul>
                  )}
                </section>
              </div>

              <h2 className="lanc-subtitulo">Últimos 30 lançamentos</h2>
              <ul className="lanc-lista lanc-lista--grade">
                {ultimos.length === 0 ? semResultado("Nenhum lançamento ainda.")
                  : ultimos.map((l) => <LinhaLanc key={l.id} l={l} />)}
              </ul>
            </div>
          </div>
        </div>

        {/* ===== Modal: lançamento ===== */}
        <Modal open={openLanc} onClose={() => setOpenLanc(false)}>
          <Box sx={style}>
            <div className="modal">
              <div className="modal-header">
                <div className="modal-header1">
                  <img className="modal-icon" src="/Images/bag-green.svg" alt="" />
                  <span className="modal-title">{editandoLanc ? "Editar Lançamento" : "Adicionar Lançamento"}</span>
                </div>
              </div>

              <div className="modal-form">
                <Grid2 container spacing={1} justifyContent="space-between" sx={{ width: "100%" }}>
                  <Grid2 sx={{ width: { xs: "100%", md: 360 } }}>
                    <Autocomplete
                      options={clientesTodos}
                      value={formLanc.client}
                      getOptionLabel={(o) => o.name ?? ""}
                      isOptionEqualToValue={(a, b) => a.id === b.id}
                      noOptionsText="Nenhum cliente"
                      onChange={(_, v) => setFormLanc((f) => ({ ...f, client: v }))}
                      renderInput={(p) => <TextField {...p} label="Cliente" color="success" />}
                    />
                    <Button size="small" color="success" sx={{ textTransform: "none", mt: 0.5 }} onClick={abrirNovoCliente}>
                      + Novo cliente
                    </Button>
                  </Grid2>

                  <Grid2 sx={{ width: { xs: "100%", md: 360 } }}>
                    <FormControl fullWidth color="success">
                      <InputLabel>Profissional</InputLabel>
                      <Select label="Profissional" color="success" MenuProps={{ style: { maxHeight: 360 } }}
                        value={formLanc.user_id} onChange={(e) => trocarProfissional(e.target.value)}>
                        {profissionais.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid2>

                  <Grid2 sx={{ width: { xs: "100%", md: 170 } }}>
                    <DateField label="Data" format="DD-MM-YYYY" color="success" fullWidth maxDate={dayjs()}
                      value={formLanc.date} onChange={(v) => setFormLanc((f) => ({ ...f, date: v }))} />
                  </Grid2>
                  <Grid2 sx={{ width: { xs: "100%", md: 170 } }} />

                  {/* serviço + botão adicionar */}
                  <Grid2 sx={{ width: "100%", display: "flex", gap: 1 }}>
                    <FormControl fullWidth color="success" disabled={!formLanc.user_id}>
                      <InputLabel>Serviço</InputLabel>
                      <Select label="Serviço" color="success" MenuProps={{ style: { maxHeight: 360 } }}
                        value={formLanc.servicoSel}
                        onChange={(e) => setFormLanc((f) => ({ ...f, servicoSel: e.target.value }))}>
                        {servicosDoProfissional.length === 0 && (
                          <MenuItem disabled value="">Este profissional não tem serviços</MenuItem>
                        )}
                        {servicosDoProfissional.map((s) => (
                          <MenuItem key={s.id} value={s.id}>{s.name} — {brl(s.price)}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button sx={btnAdd} disabled={!formLanc.servicoSel}
                      onClick={() => adicionarItem("services", "servicoSel", servicosCad)}>Adicionar</Button>
                  </Grid2>
                  <Grid2 sx={{ width: "100%" }}><ListaItens titulo="Serviços" campo="services" /></Grid2>

                  {/* produto + botão adicionar */}
                  <Grid2 sx={{ width: "100%", display: "flex", gap: 1 }}>
                    <FormControl fullWidth color="success">
                      <InputLabel>Produto</InputLabel>
                      <Select label="Produto" color="success" MenuProps={{ style: { maxHeight: 360 } }}
                        value={formLanc.produtoSel}
                        onChange={(e) => setFormLanc((f) => ({ ...f, produtoSel: e.target.value }))}>
                        {produtosCad.map((p) => (
                          <MenuItem key={p.id} value={p.id}>{p.name} — {brl(p.price)}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button sx={btnAdd} disabled={!formLanc.produtoSel}
                      onClick={() => adicionarItem("products", "produtoSel", produtosCad)}>Adicionar</Button>
                  </Grid2>
                  <Grid2 sx={{ width: "100%" }}><ListaItens titulo="Produtos" campo="products" /></Grid2>
                </Grid2>

                <div className="lanc-total">
                  <span>Total</span>
                  <strong>{brl(totalLanc)}</strong>
                </div>
                {editandoLanc?.commission_value != null && (
                  <div className="lanc-comissao">
                    Comissão do profissional: {editandoLanc.commission_pct}% = {brl(editandoLanc.commission_value)}
                  </div>
                )}

                {erroLanc && <Alert severity="error" sx={{ mt: 2 }}>{erroLanc}</Alert>}

                <div className="modal-buttons">
                  <Button onClick={salvarLanc} disabled={salvandoLanc} sx={btnPrimario}>
                    {editandoLanc ? "Salvar" : "Adicionar"}
                  </Button>
                  <Button onClick={() => setOpenLanc(false)} sx={btnSecundario}>Cancelar</Button>
                </div>
              </div>
            </div>
          </Box>
        </Modal>

        {/* ===== Modal: cliente ===== */}
        <Modal open={openCli} onClose={() => setOpenCli(false)}>
          <Box sx={{ ...style, width: { xs: "100%", md: 460 } }}>
            <div className="modal">
              <div className="modal-header">
                <div className="modal-header1">
                  <img className="modal-icon" src="/Images/person.svg" alt="" />
                  <span className="modal-title">{editandoCli ? "Editar Cliente" : "Adicionar Cliente"}</span>
                </div>
              </div>
              <div className="modal-form">
                <Grid2 container spacing={1} sx={{ width: "100%" }}>
                  <Grid2 sx={{ width: "100%" }}>
                    <TextField label="Nome" fullWidth color="success" value={formCli.name}
                      onChange={(e) => setFormCli((f) => ({ ...f, name: e.target.value }))} />
                  </Grid2>
                  <Grid2 sx={{ width: "100%" }}>
                    <TextField label="Telefone" fullWidth color="success" placeholder="(99) 99999-9999"
                      inputProps={{ inputMode: "numeric", maxLength: 15 }}
                      value={formCli.phone}
                      onChange={(e) => setFormCli((f) => ({ ...f, phone: mascaraTelefone(e.target.value) }))} />
                  </Grid2>
                </Grid2>

                {erroCli && <Alert severity="error" sx={{ mt: 2 }}>{erroCli}</Alert>}

                <div className="modal-buttons">
                  <Button onClick={salvarCliente} disabled={salvandoCli} sx={btnPrimario}>
                    {editandoCli ? "Salvar" : "Adicionar"}
                  </Button>
                  <Button onClick={() => setOpenCli(false)} sx={btnSecundario}>Cancelar</Button>
                </div>
              </div>
            </div>
          </Box>
        </Modal>
      </div>
    </LocalizationProvider>
  );
}

export default Lancamentos;
